import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { queryClient, useTRPC } from '~/services/trpc'

import { toast } from '@hap/components'

export namespace TopicMutators {
  interface MutableValues {
    label?: string
    workspaceId?: string | null
  }

  export function useCreate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.topics.create.mutationOptions({
        onMutate: async (values) => {
          const queryKey = trpc.topics.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousTopics = queryClient.getQueriesData({ queryKey })

          // Optimistically add the new topic
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            const newTopic = {
              id: '__TEMP__',
              label: values.label,
              workspaceId: values.workspaceId || null,
              isActive: true,
              suggested: null,
              createdAt: new Date(),
              updatedAt: new Date(),
            }
            return old ? [...old, newTopic] : [newTopic]
          })

          return { previousTopics }
        },
        onSuccess: () => {
          toast.success('Topic created')
          queryClient.invalidateQueries({
            queryKey: trpc.topics.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to create topic')
          // Rollback optimistic update
          if (context?.previousTopics) {
            context.previousTopics.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const create = useCallback(
      async (values: { label: string; workspaceId?: string }) => mutateAsync(values),
      [mutateAsync],
    )

    return { create, isPending }
  }

  export function useUpdate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.topics.update.mutationOptions({
        onMutate: async (updates) => {
          const queryKey = trpc.topics.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousTopics = queryClient.getQueriesData({ queryKey })

          // Optimistically update the topic
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.map((topic) => {
              if (topic.id === updates.topicId) {
                return {
                  ...topic,
                  ...updates.values,
                  updatedAt: new Date(),
                }
              }
              return topic
            })
          })

          return { previousTopics }
        },
        onSuccess: () => {
          toast.success('Topic updated')
          queryClient.invalidateQueries({
            queryKey: trpc.topics.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to update topic')
          // Rollback optimistic update
          if (context?.previousTopics) {
            context.previousTopics.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const update = useCallback(
      async (values: { topicId: string; values: Partial<MutableValues> }) =>
        mutateAsync(values),
      [mutateAsync],
    )

    return { update, isPending }
  }

  export function useConfirm({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.topics.confirm.mutationOptions({
        onMutate: async (topicId) => {
          const queryKey = trpc.topics.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousTopics = queryClient.getQueriesData({ queryKey })

          // Optimistically confirm the topic
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.map((topic) => {
              if (topic.id === topicId) {
                return {
                  ...topic,
                  isActive: true,
                  updatedAt: new Date(),
                }
              }
              return topic
            })
          })

          return { previousTopics }
        },
        onSuccess: () => {
          toast.success('Topic confirmed')
          queryClient.invalidateQueries({
            queryKey: trpc.topics.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to confirm topic')
          // Rollback optimistic update
          if (context?.previousTopics) {
            context.previousTopics.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const confirm = useCallback(
      async (topicId: string) => mutateAsync(topicId),
      [mutateAsync],
    )

    return { confirm, isPending }
  }

  export function useDestroy({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.topics.destroy.mutationOptions({
        onMutate: async (topicId) => {
          const queryKey = trpc.topics.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousTopics = queryClient.getQueriesData({ queryKey })

          // Optimistically remove the topic
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.filter((topic) => topic.id !== topicId)
          })

          return { previousTopics }
        },
        onSuccess: () => {
          toast.success('Topic deleted')
          queryClient.invalidateQueries({
            queryKey: trpc.topics.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to delete topic')
          // Rollback optimistic update
          if (context?.previousTopics) {
            context.previousTopics.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const destroy = useCallback(
      async (topicId: string) => mutateAsync(topicId),
      [mutateAsync],
    )

    return { destroy, isPending }
  }
}
